import { yupResolver } from '@hookform/resolvers/yup';
import { BlogPostCustomization, IOperationResultNoValue } from '@ViewModels';
import equal from 'fast-deep-equal';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { FieldErrors, SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useErrorMessages } from '../../../../../models/hooks/appStateHooks';
import { useFindOrCreateAndUpdateBlogStyleMutation } from '../../../../../queries/CustomPageStyle/useFindOrCreateAndUpdateBlogStyleMutation';
import { useAccountContext } from '../../../../components/blogs';
import { FontSelect } from '../../../../components/FontSelect/FontSelect';
import { ColorField } from '../components/ColorField';
import { SettingCard } from '../components/SettingCard';
import { getFontSizePlaceholder, parseOptionalFontSize } from './fontSizeHelpers';
import { ResponsiveFontSizeInputs } from './ResponsiveFontSizeInputs';
import {
	DEFAULT_BLOG_BODY_FONT_SIZE_PX,
	DEFAULT_BLOG_BUTTON_FONT_SIZE_PX,
	DEFAULT_BLOG_POST_TITLE_FONT_SIZE_PX,
	DEFAULT_FONT_VALUES,
	DEFAULT_POST_FORM_VALUES,
	MAX_FONT_SIZE_PX,
	MIN_FONT_SIZE_PX,
} from './constants';
import { blogPostSchema } from './schemas';
import { TemplateFormWrapper } from './TemplateFormWrapper';

interface PostDesignFormProps {
	customContent?: BlogPostCustomization;
	onUpdateSuccess: () => void;
	onFormDirty: () => void;
	onSavingChange?: (saving: boolean) => void;
}

export interface PostDesignFormRef {
	submit?: () => void;
}

const mergePostFormValues = (customContent?: BlogPostCustomization): BlogPostCustomization => ({
	...DEFAULT_POST_FORM_VALUES,
	...customContent,
	blogTitle: {
		...DEFAULT_POST_FORM_VALUES.blogTitle,
		...(customContent?.blogTitle ?? {}),
	},
	blogHeadlines: {
		...DEFAULT_POST_FORM_VALUES.blogHeadlines,
		...(customContent?.blogHeadlines ?? {}),
	},
	blogBody: {
		...DEFAULT_POST_FORM_VALUES.blogBody,
		...(customContent?.blogBody ?? {}),
	},
	showMoreButton: {
		...DEFAULT_POST_FORM_VALUES.showMoreButton,
		...(customContent?.showMoreButton ?? {}),
	},
});

export const PostDesignForm = forwardRef<PostDesignFormRef, PostDesignFormProps>(
	({ customContent, onFormDirty, onUpdateSuccess, onSavingChange }, ref) => {
		const errorMessages = useErrorMessages();
		const { accountId, impersonationContext } = useAccountContext();

		const {
			register,
			reset,
			setValue,
			getValues,
			handleSubmit,
			formState: { errors, isDirty },
			watch,
		} = useForm<BlogPostCustomization>({
			defaultValues: DEFAULT_POST_FORM_VALUES,
			mode: 'onBlur',
			resolver: yupResolver(blogPostSchema),
		});
		const blogTitleFont = watch('blogTitle.font', DEFAULT_FONT_VALUES);
		const blogTitleFontSize = watch('blogTitle.fontSizePx');
		const blogHeadlinesFont = watch('blogHeadlines.font', DEFAULT_FONT_VALUES);
		const blogHeadlinesFontSize = watch('blogHeadlines.fontSizePx');
		const blogBodyFont = watch('blogBody.font', DEFAULT_FONT_VALUES);
		const blogBodyFontSize = watch('blogBody.fontSizePx');
		const buttonFont = watch('showMoreButton.font', DEFAULT_FONT_VALUES);
		const buttonFontSize = watch('showMoreButton.fontSizePx');

		// Watch color values for ColorField components
		const pageBackgroundColor = watch('blogBody.backgroundColor');
		const buttonBackgroundColor = watch('showMoreButton.backgroundColor');
		const buttonTextColor = watch('showMoreButton.color');
		const buttonHoverColor = watch('showMoreButton.hoverColor');

		const updateBlogPostMutation = useFindOrCreateAndUpdateBlogStyleMutation({
			onSuccess: () => {
				reset(getValues());
				onUpdateSuccess();
				toast.success('Saved changes successfully');
			},
			onError: (error: IOperationResultNoValue) => {
				errorMessages.pushApiError(error);
			},
		});

		const onValid: SubmitHandler<BlogPostCustomization> = (data, event) => {
			event?.preventDefault?.();
			event?.stopPropagation?.();

			updateBlogPostMutation.mutate({
				id: accountId,
				impersonationContext,
				style: data,
			});
		};

		const onInvalid = (validationErrors: FieldErrors<BlogPostCustomization>) => {
			const validationErrorMessages = Object.entries(validationErrors)
				.map(([field, error]) => `${field}: ${error?.message || 'invalid'}`)
				.join(', ');
			toast.error(`Validation failed: ${validationErrorMessages}`);
		};

		// Sync customContent to form values when it changes while preserving nested defaults.
		useEffect(() => {
			if (!customContent) {
				return;
			}

			const mergedCustomContent = mergePostFormValues(customContent);
			if (!equal(mergedCustomContent, getValues())) {
				reset(mergedCustomContent);
			}
		}, [customContent, getValues, reset]);

		useEffect(() => {
			if (isDirty) {
				onFormDirty();
			}
		}, [isDirty, onFormDirty]);

		useEffect(() => {
			onSavingChange?.(updateBlogPostMutation.isPending);
		}, [updateBlogPostMutation.isPending, onSavingChange]);

		useImperativeHandle(ref, () => ({
			submit: () => handleSubmit(onValid, onInvalid)(),
		}));

		return (
			<TemplateFormWrapper
				chromeless
				title='Customize Blog Post'
				isLoading={updateBlogPostMutation.isPending}
				errors={errors}
				onSubmit={handleSubmit(onValid, onInvalid)}
			>
				<SettingCard title='Background'>
					<ColorField
						id='post-page-bg'
						label='Page Background Color'
						value={pageBackgroundColor}
						fallback='#ffffff'
						onChange={color => setValue('blogBody.backgroundColor', color, { shouldDirty: true })}
					/>
				</SettingCard>

				<SettingCard title='Title'>
					<FontSelect
						initialValue={customContent?.blogTitle?.font}
						onChange={value => setValue('blogTitle.font', value, { shouldDirty: true })}
						errors={errors?.blogTitle?.font}
						value={blogTitleFont}
					/>
					<ResponsiveFontSizeInputs
						desktopError={errors?.blogTitle?.fontSizePx}
						desktopInputProps={{
							placeholder: getFontSizePlaceholder(blogTitleFontSize, DEFAULT_BLOG_POST_TITLE_FONT_SIZE_PX),
							...register('blogTitle.fontSizePx', { setValueAs: parseOptionalFontSize }),
						}}
						desktopLabel='Title Size'
						max={MAX_FONT_SIZE_PX}
						min={MIN_FONT_SIZE_PX}
						mobileError={errors?.blogTitle?.mobileFontSizePx}
						mobileInputProps={{
							placeholder: getFontSizePlaceholder(undefined, blogTitleFontSize ?? DEFAULT_BLOG_POST_TITLE_FONT_SIZE_PX),
							...register('blogTitle.mobileFontSizePx', { setValueAs: parseOptionalFontSize }),
						}}
						mobileLabel='Mobile Title Size'
						step={1}
					/>
				</SettingCard>

				<SettingCard title='Headlines'>
					<FontSelect
						initialValue={customContent?.blogHeadlines?.font}
						onChange={value => setValue('blogHeadlines.font', value, { shouldDirty: true })}
						errors={errors?.blogHeadlines?.font}
						value={blogHeadlinesFont}
					/>
					<ResponsiveFontSizeInputs
						desktopError={errors?.blogHeadlines?.fontSizePx}
						desktopInputProps={{
							placeholder: blogHeadlinesFontSize ? String(blogHeadlinesFontSize) : 'Native',
							...register('blogHeadlines.fontSizePx', { setValueAs: parseOptionalFontSize }),
						}}
						desktopLabel='Headlines Size'
						max={MAX_FONT_SIZE_PX}
						min={MIN_FONT_SIZE_PX}
						mobileError={errors?.blogHeadlines?.mobileFontSizePx}
						mobileInputProps={{
							placeholder: 'Native',
							...register('blogHeadlines.mobileFontSizePx', { setValueAs: parseOptionalFontSize }),
						}}
						mobileLabel='Mobile Headlines Size'
						step={1}
					/>
				</SettingCard>

				<SettingCard title='Body'>
					<FontSelect
						initialValue={customContent?.blogBody?.font}
						onChange={value => setValue('blogBody.font', value, { shouldDirty: true })}
						errors={errors?.blogBody?.font}
						value={blogBodyFont}
					/>
					<ResponsiveFontSizeInputs
						desktopError={errors?.blogBody?.fontSizePx}
						desktopInputProps={{
							placeholder: getFontSizePlaceholder(blogBodyFontSize, DEFAULT_BLOG_BODY_FONT_SIZE_PX),
							...register('blogBody.fontSizePx', { setValueAs: parseOptionalFontSize }),
						}}
						desktopLabel='Body Size'
						max={MAX_FONT_SIZE_PX}
						min={MIN_FONT_SIZE_PX}
						mobileError={errors?.blogBody?.mobileFontSizePx}
						mobileInputProps={{
							placeholder: getFontSizePlaceholder(undefined, blogBodyFontSize ?? DEFAULT_BLOG_BODY_FONT_SIZE_PX),
							...register('blogBody.mobileFontSizePx', { setValueAs: parseOptionalFontSize }),
						}}
						mobileLabel='Mobile Body Size'
						step={1}
					/>
				</SettingCard>

				<SettingCard title='Show More Button'>
					<FontSelect
						initialValue={customContent?.showMoreButton?.font}
						onChange={value => setValue('showMoreButton.font', value, { shouldDirty: true })}
						errors={errors?.showMoreButton?.font}
						value={buttonFont}
					/>
					<ResponsiveFontSizeInputs
						desktopError={errors?.showMoreButton?.fontSizePx}
						desktopInputProps={{
							placeholder: getFontSizePlaceholder(buttonFontSize, DEFAULT_BLOG_BUTTON_FONT_SIZE_PX),
							...register('showMoreButton.fontSizePx', { setValueAs: parseOptionalFontSize }),
						}}
						desktopLabel='Button Text Size'
						max={MAX_FONT_SIZE_PX}
						min={MIN_FONT_SIZE_PX}
						mobileError={errors?.showMoreButton?.mobileFontSizePx}
						mobileInputProps={{
							placeholder: getFontSizePlaceholder(undefined, buttonFontSize ?? DEFAULT_BLOG_BUTTON_FONT_SIZE_PX),
							...register('showMoreButton.mobileFontSizePx', { setValueAs: parseOptionalFontSize }),
						}}
						mobileLabel='Mobile Button Text Size'
						step={1}
					/>
					<ColorField
						id='post-button-bg'
						label='Color'
						value={buttonBackgroundColor}
						fallback='#599EED'
						onChange={color => setValue('showMoreButton.backgroundColor', color, { shouldDirty: true })}
					/>
					<ColorField
						id='post-button-text-color'
						label='Text Color'
						value={buttonTextColor}
						fallback='#FFFFFF'
						onChange={color => setValue('showMoreButton.color', color, { shouldDirty: true })}
					/>
					<ColorField
						id='post-button-hover-color'
						label='Hover Color'
						value={buttonHoverColor}
						fallback='#035697'
						onChange={color => setValue('showMoreButton.hoverColor', color, { shouldDirty: true })}
					/>
				</SettingCard>
			</TemplateFormWrapper>
		);
	}
);

PostDesignForm.displayName = 'PostDesignForm';
